import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink } from "react-router";
import { cn } from "@/shared/cn";

export interface WorkHeadHosts {
  title: HTMLElement | null;
  actions: HTMLElement | null;
  tabs: HTMLElement | null;
  dialog: HTMLElement | null;
}

interface WorkHeadSlot extends WorkHeadHosts {
  claimTitle: () => () => void;
  claimDialog: () => () => void;
  applicationVisible: boolean;
}

export interface PanelDialogSlot {
  host: HTMLElement | null;
  claim: () => () => void;
  applicationVisible: boolean;
}

export function WorkHeadSlotProvider({
  hosts,
  onTitleClaims,
  onDialogClaims,
  applicationVisible = true,
  children,
}: {
  hosts: WorkHeadHosts;
  onTitleClaims: (count: number) => void;
  onDialogClaims?: (count: number) => void;
  applicationVisible?: boolean;
  children: ReactNode;
}) {
  const dialogClaims = useRef(0);
  const value = useMemo<WorkHeadSlot>(() => {
    let claims = 0;
    return {
      ...hosts,
      applicationVisible,
      claimTitle: () => {
        claims += 1;
        onTitleClaims(claims);
        return () => {
          claims -= 1;
          onTitleClaims(claims);
        };
      },
      claimDialog: () => {
        dialogClaims.current += 1;
        onDialogClaims?.(dialogClaims.current);
        return () => {
          dialogClaims.current -= 1;
          onDialogClaims?.(dialogClaims.current);
        };
      },
    };
  }, [hosts, onTitleClaims, onDialogClaims, applicationVisible]);
  return <WorkHeadSlotContext.Provider value={value}>{children}</WorkHeadSlotContext.Provider>;
}

export function usePanelDialogSlot(): PanelDialogSlot | null {
  const slot = useContext(WorkHeadSlotContext);
  return useMemo(
    () =>
      slot
        ? {
            host: slot.dialog,
            claim: slot.claimDialog,
            applicationVisible: slot.applicationVisible,
          }
        : null,
    [slot],
  );
}

export function useWorkHeadAvailable(): boolean {
  return useContext(WorkHeadSlotContext) !== null;
}

export interface WorkCrumb {
  label: string;
  to: string;
}

export function WorkTitle({
  kicker,
  crumbs = [],
  children,
}: {
  kicker?: ReactNode;
  crumbs?: readonly WorkCrumb[];
  children: ReactNode;
}) {
  const slot = useContext(WorkHeadSlotContext);
  const claimTitle = slot?.claimTitle;
  useEffect(() => claimTitle?.(), [claimTitle]);
  const title = (
    <>
      {kicker != null && <p className={WORK_KICKER_CLASS}>{kicker}</p>}
      <h1 className={WORK_TITLE_CLASS}>
        <WorkTitleText crumbs={crumbs}>{children}</WorkTitleText>
      </h1>
    </>
  );
  if (!slot) return title;
  return slot.title ? createPortal(title, slot.title) : null;
}

function WorkTitleText({
  crumbs,
  children,
}: {
  crumbs: readonly WorkCrumb[];
  children: ReactNode;
}) {
  return (
    <>
      {crumbs.map((crumb) => (
        <span key={crumb.to} className="font-normal text-(--muted)">
          <Link
            to={crumb.to}
            className="rounded-[4px] text-inherit no-underline hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand)"
          >
            {crumb.label}
          </Link>
          {" / "}
        </span>
      ))}
      {children}
    </>
  );
}

export function WorkActions({ children }: { children: ReactNode }) {
  const slot = useContext(WorkHeadSlotContext);
  if (!slot) return <div className="flex items-center gap-[10px]">{children}</div>;
  return slot.actions ? createPortal(children, slot.actions) : null;
}

export type WorkTab =
  | { key: string; label: string; count?: number; to: string; end?: boolean }
  | { key: string; label: string; count?: number; selected: boolean; onSelect: () => void };

export function WorkTabs({ label, tabs }: { label: string; tabs: readonly WorkTab[] }) {
  const slot = useContext(WorkHeadSlotContext);
  const strip = <WorkTabStrip label={label} tabs={tabs} />;
  if (!slot) return strip;
  return slot.tabs ? createPortal(strip, slot.tabs) : null;
}

function WorkTabStrip({ label, tabs }: { label: string; tabs: readonly WorkTab[] }) {
  const links = tabs.every((tab) => "to" in tab);
  const Strip = links ? "nav" : "div";
  return (
    <Strip
      role={links ? undefined : "tablist"}
      aria-label={label}
      className="flex gap-[22px] overflow-x-auto px-[28px]"
    >
      {tabs.map((tab) =>
        "to" in tab ? (
          <NavLink
            key={tab.key}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) => cn(WORK_TAB_CLASS, isActive ? WORK_TAB_ON : WORK_TAB_OFF)}
          >
            {({ isActive }) => <WorkTabLabel tab={tab} active={isActive} />}
          </NavLink>
        ) : (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={tab.selected}
            onClick={tab.onSelect}
            className={cn(WORK_TAB_CLASS, tab.selected ? WORK_TAB_ON : WORK_TAB_OFF)}
          >
            <WorkTabLabel tab={tab} active={tab.selected} />
          </button>
        ),
      )}
    </Strip>
  );
}

function WorkTabLabel({ tab, active }: { tab: WorkTab; active: boolean }) {
  if (tab.count === undefined) return tab.label;
  return (
    <>
      {tab.label}{" "}
      <span
        className={cn(
          "ml-[4px] font-normal tracking-normal tabular-nums",
          active ? "text-(--muted)" : "text-(--faint)",
        )}
      >
        {tab.count}
      </span>
    </>
  );
}

export const WORK_TITLE_CLASS =
  "m-0 min-w-0 truncate text-lg font-medium tracking-[-0.01em] text-(--ink)";

const WORK_KICKER_CLASS =
  "m-0 mb-[3px] min-w-0 truncate font-(family-name:--mono) text-xs uppercase leading-none tracking-[0.06em] text-(--muted)";

const WORK_TAB_CLASS = cn(
  "shrink-0 cursor-pointer whitespace-nowrap border-b-2 pt-[14px] pb-[12px]",
  "text-xs font-medium uppercase tracking-[0.06em] no-underline",
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand)",
);
const WORK_TAB_ON = "border-(--ink) text-(--ink)";
const WORK_TAB_OFF = "border-transparent text-(--muted) hover:text-(--ink)";

const WorkHeadSlotContext = createContext<WorkHeadSlot | null>(null);
